import { FormProvider, useForm } from "react-hook-form";
import { RegionSubmission, Region } from "../shared/interfaces/Region";
import { useTranslation } from "react-i18next";
import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { InputText } from "primereact/inputtext";
import { useContext, useState } from "react";
import { ValidationMessage } from "../shared/ValidationMessage";
import { Dialog } from "primereact/dialog";
import { Col, Container, Row } from "react-bootstrap";
import { Card } from "../shared/Card";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import { Stack, Typography } from "@mui/material";
import { CancelButton } from "../shared/CancelButton";
import { AuthContext } from "../contexts/AuthContext";
import { useSiteCreation } from "../data/Settings/useSiteCreation";
import { RegionType } from "../shared/enums/Region";
import { RegionMapping, RegionMappingSubmission } from "../shared/interfaces/RegionMapping";
import { useRegionMappingCreation } from "../data/Settings/useRegionMappingCreation";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";

interface Props {
  open?: boolean;
  onClose: () => void;
  onSiteCreated: (siteId: number, siteName: string) => void;
  customerId: number;
  companyId?: number;
  existingSites: Region[] | undefined;
  refreshSiteData: () => void;
}

export function SiteCreate(props: Props) {
  const { t } = useTranslation();
  const formValidation = useForm({ mode: "onBlur" });

  const { onSiteCreated, customerId, companyId } = props;

  const altRegionNames = useAlternativeRegionNames(customerId);

  const authContext = useContext(AuthContext);
  const userCanAddSite = authContext.canI("add", "sites");

  const [showErrorToast, setShowErrorToast] = useState(false);

  const [newSite, setNewSite] = useState<Partial<Region>>({});

  const onSubmit = formValidation.handleSubmit(() => {
    props.refreshSiteData();
    if (userCanAddSite) {
      setShowErrorToast(false);

      const region = {
        ...newSite,
        customerId: customerId,
        regionType: RegionType.Site,
      };

      const siteSubmission: RegionSubmission = {
        regionName: newSite.name,
        region: region,
      };

      siteCreate.mutate(siteSubmission);
    }
  });

  const siteCreate = useSiteCreation({
    onSuccess: (siteId) => {
      const regionMapping: RegionMapping = {
        parentId: companyId,
        subjectId: siteId,
      };

      const regionMappingSubmission: RegionMappingSubmission = {
        regionMapping: regionMapping,
      };

      regionMappingCreate.mutate(regionMappingSubmission);
    },
    onError: () => setShowErrorToast(true),
  });

  const regionMappingCreate = useRegionMappingCreation({
    onSuccess: (regionMapping) => onSiteCreated(regionMapping.subjectId, newSite.name || ""),
    onError: () => setShowErrorToast(true),
  });

  function handleHide() {
    setNewSite({});
    props.onClose();
  }

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const fieldName = event.target.name as keyof RegionSubmission;
    const value = event.target.value;
    formValidation.clearErrors(fieldName);
    formValidation.setValue(fieldName, value, { shouldValidate: false });
    setNewSite({ ...newSite, [fieldName]: value });
  };

  const siteDetailsList: FormItem[] = [
    {
      label: t("settings.siteName", {
        val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
      }),
      required: true,
      value: (
        <div>
          <div>
            <InputText
              value={newSite.name?.toString() || ""}
              maxLength={200}
              className={formValidation.formState.errors.name ? "p-invalid" : undefined}
              id={"name"}
              {...formValidation.register("name", {
                value: newSite.name,
                validate: {
                  notEmpty: (value) => value && value !== "",
                  notDuplicate: (value) => !props.existingSites?.some((site) => site.name === value),
                },
              })}
              onChange={handleInputChange}
              name={"name"}
            />
          </div>
          <div>
            {formValidation.formState.errors?.name && formValidation.formState.errors.name.type === "notEmpty" && (
              <ValidationMessage
                invalidMessage={t("settings.emptySiteName", {
                  val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
                })}
                validationType="error"
                showIcon
              />
            )}
            {formValidation.formState.errors?.name && formValidation.formState.errors.name.type === "notDuplicate" && (
              <ValidationMessage
                invalidMessage={t("settings.siteNameDuplication", {
                  site: altRegionNames.data?.siteAltName ?? "$t(common.site)",
                  company: altRegionNames.data?.companyAltName ?? "$t(common.company)",
                })}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ),
    },
  ];

  const header = (
    <Typography variant="h5" component="p">{`${altRegionNames.data?.siteAltName ?? t("common.site")} `}</Typography>
  );

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} my={0.5}>
      <ProgressButton onClick={onSubmit} disabled={siteCreate.isLoading} loading={siteCreate.isLoading}>
        {" "}
        {t("common.create")}{" "}
      </ProgressButton>
      <CancelButton onClick={handleHide} disabled={siteCreate.isLoading} className="m1-1" />
    </Stack>
  );

  return (
    <FormProvider {...formValidation}>
      <Dialog
        header={header}
        footer={footer}
        visible={props.open}
        onHide={handleHide}
        breakpoints={{ "1960px": "75vw", "1300px": "90vw" }}
        style={{ width: "60vw" }}
      >
        <Container fluid>
          <Row>
            <Col xl={6} className="px-0">
              <Card
                title={t("settings.siteDetails", {
                  val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
                })}
                className="mb-3 "
              >
                <FormContainer dataList={siteDetailsList} />
              </Card>
              <div>
                {showErrorToast && (
                  <MessageToast
                    text={t("common.dataCreateError", {
                      val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
                    })}
                    mode={ToastMode.Error}
                    show={showErrorToast}
                    autoHide
                  ></MessageToast>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Dialog>
    </FormProvider>
  );
}
